import { Button, Grid, Stack, TextField, Tooltip, IconButton, Typography } from "@mui/material";
import * as React from "react";
import USBPortSelect from "../../components/device/selects/usb_port_select";
import HelpedTextField from "../../components/general/helped_text_field";
import { DeviceClass, Protocol } from "../../model/device";
import HelpedSelect from "../../components/general/helped_select";
import { isComponentDisabledForUpdates } from "../../pages/login/login_utils";
import { useParams } from "react-router-dom";
import useBackend from "../../hooks/use_backend";
import { useQuery } from "@tanstack/react-query";
import { REACT_QUERY_KEYS } from "../../enums/react_query_key_enum";
import { fetchSiteData } from "../../scripts/api_v2/site_client";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import { useTheme } from "@emotion/react";
import { pathToPortMap } from "../../components/device/selects/usb_port_select";
import { StatusIcon } from "../general/status";
import { Status as StatusEnum } from "../../model/box_status";
import { fetchSiteProperties } from "../../scripts/api_v2/properties_client";

function isInternetProtocol(protocol) {
  return protocol === Protocol.TCP || protocol === Protocol.TCP_RTU;
}

const connGridItemSx = { display: "flex", alignItems: "center", justifyContent: "left", px: 1, pt: 2 };

function ConnectionSettings({ device, setDevice, permissions, req_privilege, handleSave, handleDiscard }) {
  const [editing, setEditing] = React.useState(false);
  const theme = useTheme();

  const fmtConnection = (device) => {
    if (device.protocol === Protocol.RS485) {
      return `RS485 connection over ${pathToPortMap[device.address]} on slave ${device.slave}`;
    } else {
      return `${device.protocol} connection to ${device.address}:${device.port} on slave ${device.slave}`;
    }
  };

  if (!editing) {
    return (
      <Grid md={10.5} xs={12} item sx={connGridItemSx} pb={2}>
        <Typography variant="h7" sx={{ display: "block", width: "100%" }}>
          <b
            style={{
              color: theme.palette.text.primary_bold,
              width: "160px",
              paddingRight: 10,
              display: "inline-block",
              textAlign: "left",
            }}
          >
            Connection type:
          </b>
          <b style={{ color: "#999", display: "inline-block" }}>{fmtConnection(device)}</b>
        </Typography>
        <IconButton
          onClick={() => setEditing((prev) => !prev)}
          disabled={isComponentDisabledForUpdates(permissions, req_privilege.getPrivilegeKey())}
        >
          <EditIcon />
        </IconButton>
      </Grid>
    );
  }

  return (
    <>
      <Grid md={2} xs={12} item sx={connGridItemSx}>
        <HelpedSelect
          disabled={!editing}
          options={Protocol}
          label={"Protocol"}
          key="protocol"
          info="Select the communication protocol between elion box and device"
          selected={device.protocol}
          onChange={(e) => {
            setDevice("protocol", e.target.value, true);
          }}
        />
      </Grid>
      {isInternetProtocol(device?.protocol) ? (
        <>
          <Grid md={4} xs={12} item sx={connGridItemSx}>
            <HelpedTextField
              disabled={!editing}
              info="IP address of the device"
              label={"IP address"}
              fullWidth
              value={device.address}
              onChange={(e) => setDevice("address", e.target.value, true)}
            />
          </Grid>
          <Grid md={1.5} xs={12} item sx={connGridItemSx}>
            <HelpedTextField
              disabled={!editing}
              info="Modbus port on device (default 502)"
              key="port"
              label={"Port"}
              fullWidth
              value={device.port}
              onChange={(e) => setDevice("port", e.target.value, false)}
            />
          </Grid>
        </>
      ) : (
        <Grid md={5.5} xs={12} item sx={connGridItemSx}>
          <USBPortSelect
            disabled={!editing}
            key="usbportselect"
            value={device.address}
            onChange={(e) => {
              setDevice("address", e.target.value, true);
            }}
          />
        </Grid>
      )}
      <Grid md={2} xs={12} item sx={connGridItemSx}>
        <HelpedTextField
          disabled={!editing}
          info="Select the modbus slave/unit ID"
          key="slave"
          label="Modbus slave"
          fullWidth
          value={device.slave}
          onChange={(e) => setDevice("slave", e.target.value, false)}
        />
      </Grid>
      {editing ? (
        <Grid
          md={1}
          xs={12}
          item
          sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pl: 1, py: 2 }}
        >
          <IconButton
            onClick={() => {
              handleDiscard();
              setEditing(false);
            }}
            disabled={isComponentDisabledForUpdates(permissions, req_privilege.getPrivilegeKey())}
          >
            <CloseIcon />
          </IconButton>
          <IconButton
            onClick={() => {
              handleSave();
              setEditing((prev) => !prev);
            }}
            disabled={isComponentDisabledForUpdates(permissions, req_privilege.getPrivilegeKey())}
          >
            <SaveIcon />
          </IconButton>
        </Grid>
      ) : (
        <Grid
          md={1}
          xs={12}
          item
          sx={{ display: "flex", alignItems: "center", justifyContent: "center", pl: 1, py: 2 }}
        >
          <IconButton
            onClick={() => setEditing((prev) => !prev)}
            disabled={isComponentDisabledForUpdates(permissions, req_privilege.getPrivilegeKey())}
          >
            <EditIcon />
          </IconButton>
        </Grid>
      )}
    </>
  );
}

function BMSIPAddressSettings({ device, setBMS, permissions, req_privilege, handleSave, handleDiscard }) {
  const [editing, setEditing] = React.useState(false);
  const theme = useTheme();

  if (!editing) {
    return (
      <Grid md={10.5} xs={12} item sx={connGridItemSx} pb={2}>
        <Typography variant="h7" sx={{ display: "block", width: "100%" }}>
          <b
            style={{
              color: theme.palette.text.primary_bold,
              width: "160px",
              paddingRight: 10,
              display: "inline-block",
              textAlign: "left",
            }}
          >
            BMS IP Address:
          </b>
          <b style={{ color: "#999", display: "inline-block" }}>{device.bms_address}</b>
        </Typography>
        <IconButton
          onClick={() => setEditing(true)}
          disabled={isComponentDisabledForUpdates(permissions, req_privilege.getPrivilegeKey())}
        >
          <EditIcon />
        </IconButton>
      </Grid>
    );
  }

  return (
    <>
      <Grid md={4} xs={12} item sx={connGridItemSx}>
        <HelpedTextField
          disabled={!editing}
          info="IP address of the BMS system"
          label={"BMS IP Address"}
          fullWidth
          value={device.bms_address}
          onChange={(e) => setBMS("bms_address", e.target.value, true)}
        />
      </Grid>
      {editing ? (
        <Grid
          md={1}
          xs={12}
          item
          sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pl: 1, py: 2 }}
        >
          <IconButton
            onClick={() => {
              handleDiscard();
              setEditing(false);
            }}
            disabled={isComponentDisabledForUpdates(permissions, req_privilege.getPrivilegeKey())}
          >
            <CloseIcon />
          </IconButton>
          <IconButton
            onClick={() => {
              handleSave();
              setEditing(false);
            }}
            disabled={isComponentDisabledForUpdates(permissions, req_privilege.getPrivilegeKey())}
          >
            <SaveIcon />
          </IconButton>
        </Grid>
      ) : (
        <Grid
          md={1}
          xs={12}
          item
          sx={{ display: "flex", alignItems: "center", justifyContent: "center", pl: 1, py: 2 }}
        >
          <IconButton
            onClick={() => setEditing(true)}
            disabled={isComponentDisabledForUpdates(permissions, req_privilege.getPrivilegeKey())}
          >
            <EditIcon />
          </IconButton>
        </Grid>
      )}
    </>
  );
}

function formatDate(ts) {
  if (!ts) return "";
  const d = new Date(ts * 1000);
  return (
    "since " +
    ("0" + d.getDate()).slice(-2) +
    "-" +
    ("0" + (d.getMonth() + 1)).slice(-2) +
    "-" +
    d.getFullYear() +
    " " +
    ("0" + d.getHours()).slice(-2) +
    ":" +
    ("0" + d.getMinutes()).slice(-2) +
    ":" +
    ("0" + d.getSeconds()).slice(-2)
  );
}

const fmtStatus = (status_type, last_connect, last_disconnect) => {
  if (status_type === StatusEnum.ONLINE) {
    return formatDate(last_connect);
  } else if (status_type === StatusEnum.OFFLINE) {
    return formatDate(last_disconnect);
  } else {
    return;
  }
};

function ConnectionStatus({ status, last_connect, last_disconnect }) {
  const theme = useTheme();
  const label =
    status.connection === StatusEnum.ONLINE
      ? "Connected"
      : status.connection === StatusEnum.OFFLINE
        ? "Disconnected"
        : "Unknown";
  return (
    <Grid md={10.5} xs={12} item sx={connGridItemSx}>
      <Typography variant="h7" sx={{ display: "block", width: "100%" }}>
        <b
          style={{
            color: theme.palette.text.primary_bold,
            width: "160px",
            paddingRight: 10,
            display: "inline-block",
            textAlign: "left",
          }}
        >
          Connection status:
        </b>
        <StatusIcon label={label} status={status.connection} />
        <b style={{ color: "#999", display: "inline-block", paddingLeft: 10 }}>
          {fmtStatus(status.connection, last_connect, last_disconnect)}
        </b>
      </Typography>
    </Grid>
  );
}

function ModbusStatus({ status }) {
  const theme = useTheme();
  const label =
    status.modbus === StatusEnum.ONLINE
      ? "Connected"
      : status.modbus === StatusEnum.OFFLINE
        ? "Disconnected"
        : "Unknown";
  return (
    <Grid md={10.5} xs={12} item sx={connGridItemSx}>
      <Typography variant="h7" sx={{ display: "block", width: "100%" }}>
        <b
          style={{
            color: theme.palette.text.primary_bold,
            width: "160px",
            paddingRight: 10,
            display: "inline-block",
            textAlign: "left",
          }}
        >
          Modbus status:
        </b>
        <StatusIcon label={label} status={status.modbus} />
      </Typography>
    </Grid>
  );
}

export function DeviceConnection({ device, handleSaveDevice, permissionMap, required_privilege }) {
  const [deviceEdits, setDeviceEdits] = React.useState({});
  const [deviceEditState, setDeviceEditState] = React.useState(device);
  const params = useParams();
  const backend = useBackend();

  let {
    data: siteData,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: [REACT_QUERY_KEYS.SITE_DATA, backend.fetch, params.site_id],
    queryFn: () => fetchSiteData(backend.fetch, params.site_id),
    enabled: false, // sitePage will refresh the query
  });

  const getDeviceProperty = (key) => {
    try {
      return siteProps.device_properties[device.device_id][key];
    } catch (e) {
      return null;
    }
  };

  React.useEffect(() => {
    setDeviceEditState(device);
  }, [device]);

  function handleDiscard() {
    setDeviceEdits({});
    setDeviceEditState(device);
  }

  function handleSave() {
    handleSaveDevice(deviceEdits, deviceEditState);
  }

  const {
    data: siteProps,
    error: sitePropsError,
    isLoading: sitePropsLoading,
    refetch: sitePropsRefetch,
  } = useQuery({
    queryKey: [REACT_QUERY_KEYS.SITE_PROPERTIES, backend.fetch, params.site_id],
    queryFn: () => fetchSiteProperties(backend.fetch, params.site_id),
  });

  function handleDeviceChange(field, value, isString = false) {
    const processed = isString
      ? value.toString()
      : typeof value == "boolean" || typeof value == "number"
        ? value
        : value.length > 0
          ? parseInt(value)
          : 0;
    setDeviceEdits((prev) => {
      const updated = { ...prev };
      updated[field] = {
        device_id: deviceEditState.device_id,
        field: field,
        value: processed,
        prev_value: device[field],
      };
      return updated;
    });
    setDeviceEditState((prev) => {
      const updated = { ...prev };
      updated[field] = isString ? value.toString() : value;
      return updated;
    });
  }

  if (!siteData || !device) return;

  return (
    <Grid container display="flex" alignSelf="flex-start" justifyContent="center">
      <ConnectionSettings
        device={deviceEditState}
        setDevice={handleDeviceChange}
        handleSave={handleSave}
        handleDiscard={handleDiscard}
        permissions={permissionMap}
        req_privilege={required_privilege}
      />
      {deviceEditState?.device_type === "EnjoyPower" && (
        <BMSIPAddressSettings
          device={deviceEditState}
          setBMS={handleDeviceChange}
          handleSave={handleSave}
          handleDiscard={handleDiscard}
          permissions={permissionMap}
          req_privilege={required_privilege}
        />
      )}
      <ConnectionStatus
        status={device?.status}
        last_connect={getDeviceProperty("last_connect")}
        last_disconnect={getDeviceProperty("last_disconnect")}
      />
      <ModbusStatus status={device?.status} />
    </Grid>
  );
}
